import { useSnackbar } from '@components/snackbar';
import cn from 'classnames';
import { isValid, parseISO } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useTaskAccess } from 'src/features/files/hooks/useTaskAccess';

import { useTasksStore } from '../../store/useTasksStore';
import { Task } from '../../types/tasks';
import { DateRangePicker } from '../DatePicker';

export const DateFieldComponent = ({
  task,
  fieldId,
  data,
  disabled,
}: {
  task: Partial<Task>;
  fieldId: string;
  label: string;
  data: any;
  handleDelete: (flag: boolean) => void;
  disabled?: boolean;
}): JSX.Element => {
  const { isReadOnly } = useTaskAccess(task.id || '');

  const [isFocused, setIsFocused] = useState(false);
  const dateToSet =
    Array.isArray(data) && data.length <= 2
      ? data
          .map((date) => {
            // check if date is valid
            const parsedDate = parseISO(date); //parse(date, 'P', new Date(), { locale: enGB });
            const isValidDate = isValid(parsedDate);
            return isValidDate ? parseISO(date) : new Date();
          })
          .filter((date) => !!date)
      : [new Date()];

  const [dates, setDates] = React.useState<Date[] | [Date, Date] | null>(dateToSet);

  useEffect(() => {
    setDates(dateToSet);
  }, [data]);

  const onUpdateCustomFieldData = useTasksStore((s) => s.onUpdateCustomFieldData);
  const onUpdateCustomTemplateFieldData = useTasksStore((s) => s.onUpdateCustomTemplateFieldData);
  const [open] = useSnackbar();

  const handleChange = (values) => {
    if (values.length) {
      values.map((date) => (date ? parseISO(date) : undefined)).filter((date) => !!date);
    }
    setDates(values);
  };

  const handleOnDone = async () => {
    setIsFocused(false);
    if (task?.id) {
      const payload = {
        taskId: task.id,
        fieldId: fieldId,
        data: dates?.map((date) => date.toISOString()) || [],
      };
      let success = false;
      if (task.board?.id === undefined) {
        payload['taskTemplateRef'] = task.id;
        success = await onUpdateCustomTemplateFieldData(payload);
      } else {
        payload['taskTemplateRef'] = undefined;
        success = await onUpdateCustomFieldData(payload);
      }
      if (!success) {
        open({
          text: 'Failed to update field data',
          snackType: 'error',
          position: 'top-center',
          duration: 3000,
        });
      }
    }
  };

  return (
    <DateRangePicker
      value={dates !== null ? (dates.length > 0 ? ([...dates] as [Date]) : null) : null}
      onChange={handleChange}
      onDone={handleOnDone}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        setIsFocused(false);
      }}
      placeholderText="Set Date"
      inputClassName={cn(
        'date-picker w-400 rounded-sm h-36 border-gray-200 text-left pl-15 text-sm',
        {
          'bg-white border border-gray-200': isFocused,
        }
      )}
      initialTab={dates?.length === 1 ? 'startEndDate' : 'date'}
      readOnly={disabled || isReadOnly}
    />
  );
};
